/**
 * @file ServiceDiagnosisPage.jsx
 * @description SERVICE department — Diagnosis. Pick one machine and work it
 *              top to bottom:
 *                1. what is wrong NOW — active fault codes (DTC) and active
 *                   threshold alarms, each once; click one to see it on the
 *                   machine's Visualization at that moment;
 *                2. the check sheet — the PM checklist of the service that is
 *                   due (A–H), each item marked OK / Not OK with a note, and
 *                   how complete it is; kept per service cycle;
 *                3. since the last service — every fault and threshold since
 *                   then (empty when no service is on record);
 *                4. the parts changed on the machine (and record one).
 *
 * This file belongs to the SERVICE department; its backend is
 * /api/service/diagnosis (controllers/serviceDiagnosisController.js).
 * The machine is in the URL (?machine=) so a diagnosis can be shared.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Card, Stack, Typography, Chip, Autocomplete, TextField, Skeleton, Alert, Button, LinearProgress,
    ToggleButtonGroup, ToggleButton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions, Collapse, IconButton, Tooltip, CircularProgress, MenuItem,
} from '@mui/material';
import {
    Memory as DtcIcon, ReportProblem as ThresholdIcon, CheckCircle as OkIcon, Cancel as NotOkIcon,
    ExpandMore as MoreIcon, ExpandLess as LessIcon, Build as PartsIcon, Add as AddIcon,
    History as HistoryIcon, Troubleshoot as DiagnosisIcon, FactCheck as SheetIcon,
} from '@mui/icons-material';
import serviceDiagnosisService from '../../../services/serviceDiagnosisService';
import { formatToIST } from '../../../utils/dataUtils';

const when = (t, f = 'dd MMM, HH:mm') => (t ? formatToIST(t, f) : '—');
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const errText = (e, fallback) => {
    const raw = String(e?.message || '');
    try { return JSON.parse(raw.slice(raw.indexOf('{'))).error || fallback; } catch { return raw || fallback; }
};
const label = (name) => String(name || '').replace(/^THRESH_|^FAULT_/, '').replace(/_/g, ' ');

/** One "now" box: the active fault codes, or the active thresholds. */
const NowBox = ({ title, icon, rows, empty, onOpen, tone }) => (
    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2, flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
            {icon}
            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
            <Chip size="small" label={rows.length} color={rows.length ? 'error' : 'default'} sx={{ ml: 'auto', fontWeight: 700 }} />
        </Stack>
        {rows.length === 0 ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2, color: 'success.main' }}>
                <OkIcon fontSize="small" /><Typography variant="body2" fontWeight={600}>{empty}</Typography>
            </Stack>
        ) : (
            <Stack spacing={0.5}>
                {rows.map(r => (
                    <Box key={r.name} onClick={() => onOpen(r)} tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') onOpen(r); }}
                        aria-label={`${r.name} — open it on the machine`}
                        sx={{ p: 1.25, borderRadius: '10px', cursor: 'pointer', bgcolor: tone,
                              '&:hover': { boxShadow: 2 } }}>
                        <Typography variant="body2" fontWeight={800}>{r.name}</Typography>
                        {r.message && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.message}</Typography>}
                        <Typography variant="caption" color="text.secondary">
                            Since {when(r.since)}{r.reports > 1 ? ` · ${r.reports} reports` : ''}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        )}
    </Card>
);

/** One check-sheet item: OK / Not OK, a note, and the steps on demand. */
const CheckItem = ({ item, onSave, busy }) => {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState(item.note || '');
    useEffect(() => { setNote(item.note || ''); }, [item.note]);
    return (
        <Box sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Chip size="small" label={item.code} variant="outlined" sx={{ fontWeight: 700, minWidth: 48 }} />
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{item.title}</Typography>
                    {item.steps?.length > 0 && (
                        <Tooltip title={open ? 'Hide how' : 'How to check'}>
                            <IconButton size="small" aria-label={`How to check ${item.code}`} onClick={() => setOpen(o => !o)}>
                                {open ? <LessIcon fontSize="small" /> : <MoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                <ToggleButtonGroup size="small" exclusive value={item.status} disabled={busy}
                    onChange={(_e, v) => onSave(item, v, note)} aria-label={`Result of ${item.code}`}>
                    <ToggleButton value="OK" color="success" aria-label={`${item.code} OK`}><OkIcon fontSize="small" sx={{ mr: 0.5 }} />OK</ToggleButton>
                    <ToggleButton value="NOT_OK" color="error" aria-label={`${item.code} not OK`}><NotOkIcon fontSize="small" sx={{ mr: 0.5 }} />Not OK</ToggleButton>
                </ToggleButtonGroup>
                <TextField size="small" placeholder="Note" value={note} sx={{ width: { md: 240 } }}
                    inputProps={{ 'aria-label': `Note for ${item.code}` }}
                    onChange={(e) => setNote(e.target.value)}
                    onBlur={() => { if (note !== (item.note || '')) onSave(item, item.status, note); }} />
            </Stack>
            {item.checked_at && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                    {item.checked_by_name || '—'} · {when(item.checked_at)}
                </Typography>
            )}
            <Collapse in={open}>
                <Box component="ol" sx={{ m: 0, mt: 0.75, pl: 3, color: 'text.secondary' }}>
                    {(item.steps || []).map((s, i) => <Typography component="li" variant="caption" key={i}>{s}</Typography>)}
                </Box>
            </Collapse>
        </Box>
    );
};

const ServiceDiagnosisPage = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const machineId = params.get('machine') || '';
    const [machines, setMachines] = useState(null);
    const [level, setLevel] = useState(null);             // null → the server's pick (the due service)
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [partOpen, setPartOpen] = useState(false);
    const [part, setPart] = useState({ part_number: '', part_name: '', quantity: 1, changed_on: today(), note: '' });

    useEffect(() => {
        serviceDiagnosisService.listMachines()
            .then(r => setMachines(r.data || []))
            .catch(e => { setMachines([]); setError(errText(e, 'Failed to load machines')); });
    }, []);

    const load = useCallback(async () => {
        if (!machineId) { setData(null); return; }
        setLoading(true);
        try {
            const r = await serviceDiagnosisService.get(machineId, level);
            setData(r);
            setError('');
        } catch (e) {
            setData(null);
            setError(errText(e, 'Failed to load the diagnosis'));
        } finally { setLoading(false); }
    }, [machineId, level]);
    useEffect(() => { load(); }, [load]);

    const picked = useMemo(() => (machines || []).find(m => m.equipment_id === machineId) || null, [machines, machineId]);
    const pick = (m) => { setLevel(null); setParams(m ? { machine: m.equipment_id } : {}); };

    // Same deep link as Errors / Control Panel: the day loads, the frame nearest
    // the time is selected, a banner names the alarm.
    const openViz = (name, at) => {
        const t = at ? new Date(at) : null;
        const base = `/mewp-detail/${encodeURIComponent(machineId)}`;
        if (!t || isNaN(t)) { navigate(base); return; }
        const day = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        navigate(`${base}?${new URLSearchParams({ view: 'viz', date: day, at: t.toISOString(), alarm: name })}`);
    };

    const saveCheck = async (item, status, note) => {
        setSaving(true);
        try {
            const r = await serviceDiagnosisService.saveCheck(machineId, item.code, { status: status || null, note });
            setData(d => {
                const items = d.checklist.items.map(i => (i.code === item.code ? { ...i, ...r.item } : i));
                return { ...d, checklist: { ...d.checklist, items, done: items.filter(i => i.status).length } };
            });
        } catch (e) {
            setError(errText(e, 'Could not save the check'));
        } finally { setSaving(false); }
    };

    const addPart = async () => {
        setSaving(true);
        try {
            await serviceDiagnosisService.addPart(machineId, { ...part, quantity: Number(part.quantity) });
            setPartOpen(false);
            setPart({ part_number: '', part_name: '', quantity: 1, changed_on: today(), note: '' });
            load();
        } catch (e) {
            setError(errText(e, 'Could not record the part'));
        } finally { setSaving(false); }
    };

    const sheet = data?.checklist;
    const groups = useMemo(() => {
        const g = new Map();
        for (const it of sheet?.items || []) {
            if (!g.has(it.checklist)) g.set(it.checklist, { code: it.checklist, desc: it.interval_desc, items: [] });
            g.get(it.checklist).items.push(it);
        }
        return [...g.values()];
    }, [sheet]);
    const pct = sheet?.total ? Math.round((sheet.done / sheet.total) * 100) : 0;
    const ls = data?.last_service;

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {/* The machine */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <Autocomplete sx={{ width: { xs: '100%', md: 420 } }} options={machines || []} loading={machines === null}
                    value={picked} onChange={(_e, m) => pick(m)}
                    getOptionLabel={(m) => `${m.serial_number || m.equipment_id}${m.model ? ` · ${m.model}` : ''}`}
                    isOptionEqualToValue={(a, b) => a.equipment_id === b.equipment_id}
                    renderOption={(props, m) => (
                        <li {...props} key={m.equipment_id}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight={700}>{m.serial_number || m.equipment_id}</Typography>
                                    <Typography variant="caption" color="text.secondary">{[m.model, m.equipment_id].filter(Boolean).join(' · ')}</Typography>
                                </Box>
                                {m.open_dtc > 0 && <Chip size="small" color="error" label={`${m.open_dtc} DTC`} />}
                                {m.open_threshold > 0 && <Chip size="small" color="warning" label={`${m.open_threshold} threshold`} />}
                            </Stack>
                        </li>
                    )}
                    renderInput={(p) => <TextField {...p} label="Machine" placeholder="Serial number or model" />} />
                {data && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {data.machine.engine_hours != null && <Chip label={`${data.machine.engine_hours.toLocaleString('en-IN')} engine h`} sx={{ fontWeight: 700 }} />}
                        <Chip variant="outlined" sx={{ fontWeight: 700 }}
                            label={ls ? `Last service ${when(ls.date, 'dd MMM yyyy')}${ls.type ? ` · ${ls.type}` : ''}` : 'No service on record'} />
                    </Stack>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {!machineId ? (
                <Card variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: '16px' }}>
                    <DiagnosisIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="text.secondary">Pick a machine to diagnose</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Its active faults and thresholds, the check sheet, what happened since the last service and the parts changed.
                    </Typography>
                </Card>
            ) : loading && !data ? (
                <Stack spacing={2}>{[160, 320, 200].map((h, i) => <Skeleton key={i} variant="rounded" height={h} />)}</Stack>
            ) : !data ? null : (
                <Stack spacing={2}>
                    {/* 1. Now */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <NowBox title="Fault codes (DTC)" icon={<DtcIcon color="error" />} rows={data.current.dtc}
                            empty="No active fault codes" tone="error.light" onOpen={(r) => openViz(r.name, r.last_seen)} />
                        <NowBox title="Thresholds" icon={<ThresholdIcon color="warning" />} rows={data.current.threshold}
                            empty="Every reading within its limits" tone="warning.light" onOpen={(r) => openViz(r.name, r.last_seen)} />
                    </Stack>

                    {/* 2. The check sheet */}
                    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
                            <SheetIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight={700}>Check sheet</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {ls ? `For this service cycle — since ${when(ls.date, 'dd MMM yyyy')}` : 'No service on record — kept until the first one'}
                                </Typography>
                            </Box>
                            <TextField select size="small" label="Service" value={sheet.level} sx={{ minWidth: 220 }}
                                onChange={(e) => setLevel(e.target.value)}>
                                {sheet.levels.map(l => <MenuItem key={l.code} value={l.code}>{l.code} — {l.interval_desc}</MenuItem>)}
                            </TextField>
                        </Stack>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                            <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                color={pct === 100 ? 'success' : 'primary'} aria-label="Check sheet completeness" />
                            <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                                {sheet.done} of {sheet.total} checked · {pct}%
                            </Typography>
                        </Stack>
                        {groups.map(g => (
                            <Box key={g.code} sx={{ mt: 1.5 }}>
                                <Typography variant="overline" color="text.secondary">{g.code} · {g.desc}</Typography>
                                {g.items.map(it => <CheckItem key={it.code} item={it} onSave={saveCheck} busy={saving} />)}
                            </Box>
                        ))}
                    </Card>

                    {/* 3. Since the last service */}
                    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <HistoryIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight={700}>Since the last service</Typography>
                            {ls && <Typography variant="caption" color="text.secondary">
                                {when(ls.date, 'dd MMM yyyy')} · {ls.label}{ls.hmr != null ? ` · ${ls.hmr} h` : ''}
                            </Typography>}
                        </Stack>
                        {!data.since_last_service ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No service on record for this machine.</Typography>
                        ) : data.since_last_service.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No faults or thresholds since then.</Typography>
                        ) : (
                            <TableContainer sx={{ maxHeight: 420 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead><TableRow>
                                        <TableCell>Time</TableCell><TableCell>Type</TableCell><TableCell>Alarm</TableCell>
                                        <TableCell>Details</TableCell><TableCell>Status</TableCell>
                                    </TableRow></TableHead>
                                    <TableBody>
                                        {data.since_last_service.map(a => (
                                            <TableRow key={`${a.id}`} hover sx={{ cursor: 'pointer' }} onClick={() => openViz(a.name, a.created_at)}>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(a.created_at)}</TableCell>
                                                <TableCell><Chip size="small" variant="outlined" label={a.kind === 'DTC' ? 'Fault code' : 'Threshold'} /></TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>{label(a.name)}</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>{a.message || '—'}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={a.status} color={a.status === 'Active' ? 'error' : 'success'}
                                                        variant={a.status === 'Active' ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
                                                    {a.closed_at && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>cleared {when(a.closed_at)}</Typography>}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>

                    {/* 4. Parts changed */}
                    <Card variant="outlined" sx={{ borderRadius: '16px', p: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <PartsIcon color="primary" />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>Parts changed</Typography>
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setPartOpen(true)}>Record a part</Button>
                        </Stack>
                        {data.parts.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No parts recorded for this machine.</Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead><TableRow>
                                        <TableCell>Date</TableCell><TableCell>Part no.</TableCell><TableCell>Part</TableCell>
                                        <TableCell align="right">Qty</TableCell><TableCell>Note</TableCell><TableCell>Recorded by</TableCell>
                                    </TableRow></TableHead>
                                    <TableBody>
                                        {data.parts.map(p => (
                                            <TableRow key={p.id}>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(`${p.changed_on}T00:00:00+05:30`, 'dd MMM yyyy')}</TableCell>
                                                <TableCell>{p.part_number || '—'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>{p.part_name}</TableCell>
                                                <TableCell align="right">{p.quantity}</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>{p.note || '—'}</TableCell>
                                                <TableCell>{p.recorded_by_name || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                </Stack>
            )}

            <Dialog open={partOpen} onClose={() => !saving && setPartOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Record a part changed</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField label="Part name" required value={part.part_name}
                            onChange={(e) => setPart(p => ({ ...p, part_name: e.target.value }))} />
                        <TextField label="Part number" value={part.part_number}
                            onChange={(e) => setPart(p => ({ ...p, part_number: e.target.value }))} />
                        <Stack direction="row" spacing={2}>
                            <TextField label="Quantity" type="number" value={part.quantity} inputProps={{ min: 0.01, step: 'any' }}
                                onChange={(e) => setPart(p => ({ ...p, quantity: e.target.value }))} />
                            <TextField label="Changed on" type="date" value={part.changed_on} InputLabelProps={{ shrink: true }}
                                inputProps={{ max: today() }} onChange={(e) => setPart(p => ({ ...p, changed_on: e.target.value }))} />
                        </Stack>
                        <TextField label="Note" value={part.note} onChange={(e) => setPart(p => ({ ...p, note: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPartOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={addPart}
                        disabled={saving || !part.part_name.trim() || !(Number(part.quantity) > 0) || !part.changed_on}
                        startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}>
                        Record
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ServiceDiagnosisPage;
